"use client";
import ReactDOM from "react-dom";
import { useEffect, useRef, useState } from "react";
import { Logger } from "@shared/utils/logger";

interface PipPortalProps {
  children: React.ReactNode;
  pipWindow: Window;
}

/**
 * PipPortal - Renders children into the Picture-in-Picture window using React Portal.
 * Also handles copying styles from the main window.
 */
export const PipPortal = ({ children, pipWindow }: PipPortalProps) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const pipWindowRef = useRef(pipWindow);
  pipWindowRef.current = pipWindow;

  useEffect(() => {
    const pip = pipWindowRef.current;
    if (!pip) return;

    // Setup document structure
    const setupPipDocument = () => {
      // Add viewport meta tag for better mobile rendering
      const viewportMeta = pip.document.createElement("meta");
      viewportMeta.name = "viewport";
      viewportMeta.content = "width=device-width, initial-scale=1.0";
      pip.document.head.appendChild(viewportMeta);

      // Add charset meta tag
      const charsetMeta = pip.document.createElement("meta");
      charsetMeta.setAttribute("charset", "UTF-8");
      pip.document.head.appendChild(charsetMeta);

      // Set document title
      pip.document.title = "Meeting - Picture in Picture";

      // Set body styles
      pip.document.body.style.margin = "0";
      pip.document.body.style.padding = "0";
      pip.document.body.style.overflow = "hidden";
      pip.document.body.style.backgroundColor = "#111111";
    };

    setupPipDocument();

    // Create a container div in the PiP window
    const div = pip.document.createElement("div");
    div.id = "pip-root";
    div.style.height = "100%";
    div.style.width = "100%";
    pip.document.body.appendChild(div);
    setContainer(div);

    // Copy styles from main window to PiP window
    const copyStyles = () => {
      const styles = Array.from(document.styleSheets);
      let copiedCount = 0;
      let failedCount = 0;

      styles.forEach((styleSheet) => {
        try {
          if (styleSheet.href) {
            // Link external stylesheets (Tailwind, etc.)
            const newLink = pip.document.createElement("link");
            newLink.rel = "stylesheet";
            newLink.href = styleSheet.href;
            newLink.type = "text/css";
            pip.document.head.appendChild(newLink);
            copiedCount++;
          } else if (styleSheet.cssRules) {
            // Copy inline styles
            const newStyle = pip.document.createElement("style");
            try {
              Array.from(styleSheet.cssRules).forEach((rule) => {
                newStyle.appendChild(pip.document.createTextNode(rule.cssText));
              });
              pip.document.head.appendChild(newStyle);
              copiedCount++;
            } catch (innerError) {
              // CORS or access issues with specific rules
              Logger.warn("PipPortal", "Failed to copy individual CSS rules", innerError);
              failedCount++;
            }
          }
        } catch (e) {
          Logger.warn("PipPortal", "Failed to copy stylesheet", e);
          failedCount++;
        }
      });

      Logger.info("PipPortal", `Style copying complete: ${copiedCount} succeeded, ${failedCount} failed`);
    };

    copyStyles();

    // Copy font faces to PiP window for consistent typography
    const copyFonts = () => {
      try {
        if (document.fonts && pip.document.fonts) {
          document.fonts.forEach((font) => {
            if (font.status === "loaded") {
              pip.document.fonts.add(font);
            }
          });
        }
      } catch (e) {
        Logger.warn("PipPortal", "Failed to copy fonts", e);
      }
    };

    copyFonts();

    // Cleanup
    return () => {
      if (div && pip.document.body.contains(div)) {
        pip.document.body.removeChild(div);
      }
    };
  }, [pipWindow]);

  if (!container) return null;

  return ReactDOM.createPortal(children, container);
};
